/**
 * Certificados odontológicos: de atención (asistió a consulta tal día y hora) y de tratamiento con reposo
 * (máximo 3 días) para cirugías que lo justifican. El texto se arma aquí y se congela al firmar.
 * El formato debe revisarlo un abogado; los afiliados al IESS pueden necesitar validar el reposo en el IESS.
 */
import { descripcionCie } from "./cie10.ts";

export const TIPOS_CERTIFICADO = ["atencion", "reposo"] as const;
export type TipoCertificado = (typeof TIPOS_CERTIFICADO)[number];
export const MAX_DIAS_REPOSO = 3;

export interface ProcedimientoReposo { clave: string; nombre: string; cie10: string; dias: number }
export const PROCEDIMIENTOS_REPOSO: readonly ProcedimientoReposo[] = [
  { clave: "terceros_molares", nombre: "Extracción quirúrgica de terceros molares", cie10: "K01.1", dias: 3 },
  { clave: "canino_incluido", nombre: "Extracción quirúrgica de canino incluido", cie10: "K01.0", dias: 3 },
  { clave: "drenaje_absceso", nombre: "Drenaje de absceso", cie10: "K04.7", dias: 2 },
  { clave: "cirugia_apical", nombre: "Cirugía apical (apicectomía)", cie10: "K04.5", dias: 2 },
  { clave: "cirugia_periodontal", nombre: "Cirugía periodontal", cie10: "K05.3", dias: 2 },
  { clave: "cirugia_implantes", nombre: "Cirugía de implantes dentales", cie10: "K08.1", dias: 2 },
];
export const procedimientoReposo = (clave: string) => PROCEDIMIENTOS_REPOSO.find((p) => p.clave === clave);

const UNIDADES = ["cero", "un", "dos", "tres"];
const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
export const fechaCertificado = (f: string) => { const [a, m, d] = f.split("-").map(Number); return `${d} de ${MESES[m - 1]} de ${a}`; };
const diasEnLetras = (n: number) => `${UNIDADES[n] ?? n} (${n}) día${n === 1 ? "" : "s"}`;

/** Último día de reposo (inclusive): 3 días desde el lunes → hasta el miércoles. */
export function finReposo(desde: string, dias: number): string {
  const d = new Date(desde + "T12:00:00Z");
  d.setUTCDate(d.getUTCDate() + Math.max(dias, 1) - 1);
  return d.toISOString().slice(0, 10);
}

export interface DatosCertificado {
  tipo: TipoCertificado;
  paciente: { nombre: string; doc: string; edad: number | null; representante?: string | null };
  fecha: string; horaDesde: string; horaHasta: string;
  procedimiento: string; diagnostico: string; cie10: string | null; piezas: number[];
  dias: number; desde: string | null; destinatario: string;
  profesional: { nombre: string; registro: string }; clinica: string; ciudad: string;
}

export function problemasCertificado(d: Pick<DatosCertificado, "tipo" | "fecha" | "horaDesde" | "horaHasta" | "procedimiento" | "diagnostico" | "dias" | "desde">, hoy: string): string[] {
  const p: string[] = [];
  const hora = /^([01]\d|2[0-3]):[0-5]\d$/;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d.fecha)) p.push("La fecha de atención no es válida.");
  else if (d.fecha > hoy) p.push("La fecha de atención no puede ser futura.");
  if (!hora.test(d.horaDesde) || !hora.test(d.horaHasta)) p.push("Indica la hora de llegada y de salida (hh:mm).");
  else if (d.horaHasta <= d.horaDesde) p.push("La hora de salida debe ser posterior a la de llegada.");
  if (!d.diagnostico.trim()) p.push("Indica el diagnóstico.");
  if (d.tipo === "reposo") {
    if (!d.procedimiento.trim()) p.push("Indica el procedimiento realizado.");
    if (!Number.isInteger(d.dias) || d.dias < 1 || d.dias > MAX_DIAS_REPOSO) p.push(`El reposo debe ser de 1 a ${MAX_DIAS_REPOSO} días.`);
    if (!d.desde || !/^\d{4}-\d{2}-\d{2}$/.test(d.desde)) p.push("Indica desde qué fecha rige el reposo.");
    else if (d.desde < d.fecha) p.push("El reposo no puede empezar antes de la atención.");
    else if (d.fecha && (Date.parse(d.desde) - Date.parse(d.fecha)) / 86400000 > 1) p.push("El reposo debe empezar el día de la atención o el siguiente.");
  }
  return p;
}

/** Texto formal del certificado (se congela al firmar). */
export function textoCertificado(d: DatosCertificado): string {
  const quien = `${d.paciente.nombre}${d.paciente.doc ? `, con cédula de identidad N.º ${d.paciente.doc}` : ""}${d.paciente.edad !== null ? `, de ${d.paciente.edad} años de edad` : ""}`;
  const menor = d.paciente.representante ? `, acompañado(a) por su representante ${d.paciente.representante}` : "";
  const dx = `${d.diagnostico.trim().replace(/\.$/, "")}${d.cie10 ? ` (CIE-10 ${d.cie10}${descripcionCie(d.cie10) && !d.diagnostico.includes(descripcionCie(d.cie10)) ? `: ${descripcionCie(d.cie10)}` : ""})` : ""}`;
  const piezas = d.piezas.length ? ` en ${d.piezas.length === 1 ? "la pieza" : "las piezas"} ${d.piezas.join(", ")}` : "";
  const partes = [
    `${d.ciudad ? `${d.ciudad}, ` : ""}${fechaCertificado(d.fecha)}`,
    d.tipo === "reposo" ? "CERTIFICADO ODONTOLÓGICO DE REPOSO" : "CERTIFICADO DE ATENCIÓN ODONTOLÓGICA",
    `Certifico que ${quien}${menor}, fue atendido(a) en ${d.clinica} el día ${fechaCertificado(d.fecha)}, desde las ${d.horaDesde} hasta las ${d.horaHasta}, con diagnóstico de ${dx}.`,
    d.procedimiento.trim() ? `Se realizó: ${d.procedimiento.trim().replace(/\.$/, "")}${piezas}.` : "",
    d.tipo === "reposo" && d.desde
      ? `Por el procedimiento realizado, el/la paciente requiere reposo de ${diasEnLetras(d.dias)}, desde el ${fechaCertificado(d.desde)} hasta el ${fechaCertificado(finReposo(d.desde, d.dias))}, inclusive.`
      : "",
    `Es todo cuanto puedo certificar en honor a la verdad; el/la interesado(a) puede hacer uso del presente ${d.destinatario.trim() ? `para ser presentado en ${d.destinatario.trim().replace(/\.$/, "")}` : "como estime conveniente"}.`,
  ];
  return partes.filter(Boolean).join("\n\n");
}
